import { forwardRef, useRef } from "react";
import { useStyles } from "./Button.style";
import clsx from "classnames";
import { Icon } from "../Icon/Icon";
import { CircularProgress } from "../CircularProgress";

type ButtonVariant = "contained" | "outlined" | "borderless";
type ButtonSize = "small" | "large";
type ButtonColor = "primary" | "highlight";

export interface IButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  disabledRipple?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  color?: ButtonColor;
  icon?: string | React.ReactNode;
  iconPosition?: "start" | "end";
}

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  ({
    className,
    variant = "contained",
    size = "large",
    color = "primary",
    disabled = false,
    fullWidth = false,
    disabledRipple,
    children,
    icon,
    iconPosition = "end",
    loading,
    ...otherProps
  }) => {
    const classes = useStyles();
    const currentButtonRef = useRef<HTMLButtonElement>(null);

    function createRipple(event: React.TouchEvent<HTMLButtonElement>) {
      if (disabled || disabledRipple) return;

      const button = event.currentTarget;
      const circle = document.createElement("span");
      const diameter = Math.max(button.clientWidth, button.clientHeight);
      circle.style.width = circle.style.height = `${diameter}px`;
      circle.classList.add("ripple");

      const ripple = button.getElementsByClassName("ripple")[0];
      if (ripple) {
        ripple.remove();
      }

      button.appendChild(circle);
    }

    return (
      <button
        ref={currentButtonRef}
        onTouchStart={createRipple}
        id="button"
        {...otherProps}
        className={clsx(
          classes.root,
          variant,
          size,
          color,
          { disabled },
          { fullWidth },
          icon && !children && "only-icon",
          iconPosition,
          className,
        )}
      >
        {color === "primary" && !disabled && (
          <div className={classes.overlay} />
        )}
        {loading ? (
          <CircularProgress enableAnimation size={20} borderLight />
        ) : (
          <>
            {children}
            {typeof icon === "string" ? (
              <Icon name={icon} size={size === "small" ? 16 : 20} />
            ) : (
              icon
            )}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
